import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiUrl } from "../../config/api";
import { META_MARKETING_URL } from "./tableeMarketingTemplates";
import "./tableeMarketing.css";

const emptyMetaIntegration = {
  provider: "meta",
  isEnabled: false,
  appId: "",
  appSecret: "",
  appSecretConfigured: false,
  appSecretPreview: "",
  accessToken: "",
  accessTokenConfigured: false,
  accessTokenPreview: "",
  pixelId: "",
  browserPixelEnabled: false,
  conversionsApiEnabled: false,
  conversionsAccessToken: "",
  conversionsAccessTokenConfigured: false,
  conversionsAccessTokenPreview: "",
  adAccountId: "",
  businessId: "",
  graphApiVersion: "v25.0",
  useTableeAssociatedSpend: false,
  status: "not_configured",
  lastError: "",
  lastCheckedAt: null,
};

const emptyGoogleIntegration = {
  provider: "google", isEnabled: false, customerId: "", managerCustomerId: "", clientId: "",
  tableeCampaignIds: "24030847091",
  clientSecret: "", clientSecretConfigured: false, refreshToken: "", refreshTokenConfigured: false,
  developerToken: "", developerTokenConfigured: false, measurementId: "", conversionId: "",
  purchaseConversionLabel: "", conversionActionId: "", ga4Enabled: false, googleAdsEnabled: false,
  dataManagerEnabled: false,
  status: "not_configured", lastError: "", lastCheckedAt: null,
};

const currencyFormatter = new Intl.NumberFormat("es-CL", {
  style: "currency",
  currency: "CLP",
  maximumFractionDigits: 0,
});

const numberFormatter = new Intl.NumberFormat("es-CL");

function getSessionHeaders() {
  const session = JSON.parse(localStorage.getItem("sessionData") || "{}");
  return {
    Authorization: session.sessionKey || "",
    Username: session.username || "",
    "Content-Type": "application/json",
  };
}

function getDefaultRange() {
  const now = new Date();
  const from = new Date(now);
  from.setDate(from.getDate() - 29);
  return {
    dateFrom: from.toISOString().slice(0, 10),
    dateTo: now.toISOString().slice(0, 10),
  };
}

function formatCurrency(value) {
  return currencyFormatter.format(Number(value || 0));
}

function formatNumber(value) {
  return numberFormatter.format(Number(value || 0));
}

function formatPercent(value) {
  return `${Number(value || 0).toLocaleString("es-CL", { maximumFractionDigits: 2 })}%`;
}

function formatDuration(milliseconds) {
  const seconds = Math.round(Number(milliseconds || 0) / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ${seconds % 60}s`;
}

function getCheckoutStepLabel(step) {
  return ({
    entry: "Ingreso",
    data: "Datos",
    delivery: "Despacho",
    payment: "Pago",
    details: "Confirmación",
  })[step] || step || "Desconocido";
}

function getCheckoutEventLabel(event) {
  return ({
    checkout_validation_error: "Error de validación",
    checkout_submission_error: "Error al crear pedido",
    checkout_navigation: "Retroceso",
    shipping_option_selected: "Despacho seleccionado",
    shipping_date_selected: "Fecha seleccionada",
    payment_method_selected: "Pago seleccionado",
    payment_initiated: "Pago iniciado",
    payment_handoff: "Enviado al proveedor",
  })[event] || event;
}

function formatRoas(revenue, spend) {
  const spendValue = Number(spend || 0);
  if (!spendValue) return "—";
  return `${(Number(revenue || 0) / spendValue).toLocaleString("es-CL", { maximumFractionDigits: 2 })}x`;
}

function getStatusLabel(status) {
  if (status === "connected") return "Conectado";
  if (status === "error") return "Error";
  if (status === "configured") return "Configurado";
  return "Sin configurar";
}

function getStatusTone(status) {
  if (status === "connected") return "success";
  if (status === "error") return "danger";
  if (status === "configured") return "warning";
  return "muted";
}

function getMetaScopeLabel(integration, storefrontName) {
  return integration.useTableeAssociatedSpend ? `Gasto asociado a ${storefrontName}` : "Cuenta Meta completa";
}

function filterAttributionForMetaScope(attributionData, metaRows, scope) {
  if (scope?.mode !== "tablee_associated") return attributionData;
  const campaignIds = new Set((metaRows || []).map((row) => String(row.campaignId || "")).filter(Boolean));
  const rows = (attributionData.rows || []).filter((row) => campaignIds.has(String(row.campaignId || "")));
  return {
    rows,
    totals: rows.reduce((totals, row) => ({
      orders: totals.orders + Number(row.orders || 0),
      revenue: totals.revenue + Number(row.revenue || 0),
      fbclidOrders: totals.fbclidOrders + Number(row.fbclidOrders || 0),
    }), { orders: 0, revenue: 0, fbclidOrders: 0 }),
  };
}

function filterAttributionForGoogleScope(attributionData, scope) {
  if (scope?.mode !== "tablee_campaigns") return attributionData;
  const campaignIds = new Set((scope.campaignIds || []).map((id) => String(id)).filter(Boolean));
  const rows = (attributionData.rows || []).filter((row) => campaignIds.has(String(row.campaignId || "")));
  return {
    rows,
    totals: rows.reduce((totals, row) => ({
      orders: totals.orders + Number(row.orders || 0),
      revenue: totals.revenue + Number(row.revenue || 0),
    }), { orders: 0, revenue: 0 }),
  };
}

function mergeCampaignRows(metaRows, tableeRows, storefrontName) {
  const tableeById = new Map();
  const tableeByName = new Map();
  tableeRows.forEach((row) => {
    if (row.campaignId) tableeById.set(String(row.campaignId), row);
    if (row.campaignName) tableeByName.set(String(row.campaignName).toLowerCase(), row);
  });

  const usedTableeRows = new Set();
  const merged = metaRows.map((row) => {
    const idMatch = row.campaignId ? tableeById.get(String(row.campaignId)) : null;
    const nameMatch = !idMatch && row.campaignName ? tableeByName.get(String(row.campaignName).toLowerCase()) : null;
    const tablee = idMatch || nameMatch || {};
    if (idMatch || nameMatch) usedTableeRows.add(tablee);
    return {
      ...row,
      tableeOrders: tablee.orders || 0,
      tableeRevenue: tablee.revenue || 0,
      matchType: idMatch ? "id" : nameMatch ? "nombre" : "",
    };
  });

  tableeRows.forEach((row) => {
    if (usedTableeRows.has(row)) return;
    merged.push({
      campaignId: row.campaignId,
      campaignName: row.campaignName,
      spend: 0,
      impressions: 0,
      reach: 0,
      clicks: 0,
      ctr: 0,
      cpc: 0,
      cpm: 0,
      tableeOrders: row.orders || 0,
      tableeRevenue: row.revenue || 0,
      matchType: row.campaignId ? `solo ${storefrontName}` : "captura local",
    });
  });

  return merged;
}

function TableeMarketing({ apiPrefix = "/tablee", storefrontName = "Tablee", publicBaseUrl, storeLogo = "/images/tablee_logo.png" } = {}) {
  const defaultGoogleIntegration = useMemo(() => ({ ...emptyGoogleIntegration, ...(apiPrefix !== "/tablee" ? { tableeCampaignIds: "" } : {}) }), [apiPrefix]);
  const metaMarketingUrl = META_MARKETING_URL.replace("https://www.tablee.cl/", `${(publicBaseUrl || "https://www.tablee.cl").replace(/\/+$/, "")}/`);
  const defaultRange = useMemo(getDefaultRange, []);
  const [activeProvider, setActiveProvider] = useState("site");
  const [trafficTab, setTrafficTab] = useState("summary");
  const [configProvider, setConfigProvider] = useState("");
  const [metaIntegration, setMetaIntegration] = useState(emptyMetaIntegration);
  const [metaDraft, setMetaDraft] = useState(emptyMetaIntegration);
  const [googleIntegration, setGoogleIntegration] = useState(defaultGoogleIntegration);
  const [googleDraft, setGoogleDraft] = useState(defaultGoogleIntegration);
  const [googleInsights, setGoogleInsights] = useState({ rows: [], totals: {}, scope: {} });
  const [googleAttribution, setGoogleAttribution] = useState({ rows: [], totals: {} });
  const [visitorAnalytics, setVisitorAnalytics] = useState({
    totals: {},
    funnel: [],
    topProducts: [],
    topAreas: [],
    sources: [],
    landingSections: [],
    landingClicks: [],
    landingFunnels: [],
    checkoutSteps: [],
    checkoutIssues: [],
  });
  const [dateRange, setDateRange] = useState(defaultRange);
  const [insights, setInsights] = useState({ rows: [], adRows: [], totals: {}, scope: {} });
  const [attribution, setAttribution] = useState({ rows: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [savingConversions, setSavingConversions] = useState(false);
  const [testingConversions, setTestingConversions] = useState(false);
  const [metaConfigTab, setMetaConfigTab] = useState("account");
  const [testEventCode, setTestEventCode] = useState("");
  const [eventStatus, setEventStatus] = useState({ pending: 0, processing: 0, sent: 0, failed: 0, expired: 0 });
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [, setMessage] = useState("");
  const [modalMessage, setModalMessage] = useState(null);
  const [showMetaUrl, setShowMetaUrl] = useState(false);
  const [metaUrlCopied, setMetaUrlCopied] = useState(false);

  const campaignRows = useMemo(
    () => mergeCampaignRows(insights.rows || [], attribution.rows || [], storefrontName),
    [insights.rows, attribution.rows, storefrontName]
  );
  const googleCampaignRows = useMemo(() => mergeCampaignRows(googleInsights.rows || [], googleAttribution.rows || [], storefrontName), [googleInsights.rows, googleAttribution.rows, storefrontName]);

  const loadIntegrations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(apiUrl(`${apiPrefix}/marketing/integrations`), {
        headers: getSessionHeaders(),
      });
      const data = await response.json();
      if (!data.success) {
        setMessage(data.message || "No se pudo cargar Marketing.");
        return;
      }
      const meta = data.integrations?.find((integration) => integration.provider === "meta") || emptyMetaIntegration;
      const nextMeta = { ...emptyMetaIntegration, ...meta, appSecret: "", accessToken: "", conversionsAccessToken: "" };
      setMetaIntegration(nextMeta);
      setMetaDraft(nextMeta);
      const google = data.integrations?.find((integration) => integration.provider === "google") || defaultGoogleIntegration;
      const nextGoogle = { ...defaultGoogleIntegration, ...google, clientSecret: "", refreshToken: "", developerToken: "" };
      setGoogleIntegration(nextGoogle);
      setGoogleDraft(nextGoogle);
    } catch (err) {
      setMessage("No se pudo cargar Marketing.");
    } finally {
      setLoading(false);
    }
  }, [apiPrefix, defaultGoogleIntegration]);

  function openGoogleConfig() {
    setGoogleDraft({ ...defaultGoogleIntegration, ...googleIntegration, clientSecret: "", refreshToken: "", developerToken: "" });
    setModalMessage(null);
    setConfigProvider("google");
  }

  async function saveGoogleIntegration(event) {
    event.preventDefault(); setSaving(true); setModalMessage(null);
    try {
      const response = await fetch(apiUrl(`${apiPrefix}/marketing/integrations/google`), { method: "PUT", headers: getSessionHeaders(), body: JSON.stringify(googleDraft) });
      const data = await response.json();
      if (!data.success) return setModalMessage({ tone: "error", text: data.message || "No se pudo guardar Google." });
      const next = { ...defaultGoogleIntegration, ...data.integration, clientSecret: "", refreshToken: "", developerToken: "" };
      setGoogleIntegration(next); setGoogleDraft(next); setConfigProvider("");
    } catch (_err) { setModalMessage({ tone: "error", text: "No se pudo guardar Google." }); }
    finally { setSaving(false); }
  }

  async function testGoogleConnection() {
    setTesting(true); setModalMessage(null);
    try {
      const response = await fetch(apiUrl(`${apiPrefix}/marketing/integrations/google/test`), { method: "POST", headers: getSessionHeaders(), body: JSON.stringify(googleDraft) });
      const data = await response.json();
      if (!data.success) {
        setModalMessage({ tone: "error", text: data.message || "Google rechazó la conexión." });
        return;
      }
      const purchaseAction = (data.conversionActions || []).find((action) => action.name === `${storefrontName} | Compra web directa`)
        || (data.conversionActions || []).find((action) => String(action.name || "").toLocaleLowerCase("es-CL").includes(storefrontName.toLocaleLowerCase("es-CL")) && /compra/i.test(action.name));
      if (purchaseAction?.id) {
        setGoogleDraft((current) => ({ ...current, conversionActionId: purchaseAction.id }));
      }
      setModalMessage({
        tone: data.dataManagerAuthorized ? "success" : "warning",
        text: `Google Ads conectado${data.account?.name ? `: ${data.account.name}` : ""}.${purchaseAction?.id ? ` Acción de compra ${purchaseAction.id} detectada.` : ""}${data.dataManagerAuthorized ? " Data Manager autorizado." : " El OAuth actual aún no incluye el permiso Data Manager."}`,
      });
    } catch (_err) { setModalMessage({ tone: "error", text: "No se pudo probar Google Ads." }); }
    finally { setTesting(false); }
  }

  function openMetaConfig(tab = "account") {
    setMetaDraft({ ...emptyMetaIntegration, ...metaIntegration, appSecret: "", accessToken: "", conversionsAccessToken: "" });
    setMetaConfigTab(tab);
    setTestEventCode("");
    setModalMessage(null);
    setShowMetaUrl(false);
    setMetaUrlCopied(false);
    setConfigProvider("meta");
  }

  function closeMetaConfig() {
    setMetaDraft({ ...emptyMetaIntegration, ...metaIntegration, appSecret: "", accessToken: "", conversionsAccessToken: "" });
    setTestEventCode("");
    setModalMessage(null);
    setConfigProvider("");
  }

  async function saveMetaIntegration(event) {
    event.preventDefault();
    setSaving(true);
    setModalMessage(null);
    try {
      const response = await fetch(apiUrl(`${apiPrefix}/marketing/integrations/meta`), {
        method: "PUT",
        headers: getSessionHeaders(),
        body: JSON.stringify(metaDraft),
      });
      const data = await response.json();
      if (!data.success) {
        setModalMessage({ tone: "error", text: data.message || "No se pudo guardar Meta Ads." });
        return;
      }
      const nextMeta = { ...emptyMetaIntegration, ...data.integration, appSecret: "", accessToken: "", conversionsAccessToken: "" };
      setMetaIntegration(nextMeta);
      setMetaDraft(nextMeta);
      setShowMetaUrl(false);
      setMetaUrlCopied(false);
      setConfigProvider("");
    } catch (err) {
      setModalMessage({ tone: "error", text: "No se pudo guardar Meta Ads." });
    } finally {
      setSaving(false);
    }
  }

  async function testMetaConnection() {
    setTesting(true);
    setModalMessage(null);
    try {
      const response = await fetch(apiUrl(`${apiPrefix}/marketing/integrations/meta/test`), {
        method: "POST",
        headers: getSessionHeaders(),
        body: JSON.stringify(metaDraft),
      });
      const data = await response.json();
      if (!data.success) {
        setModalMessage({ tone: "error", text: data.message || "Meta rechazó la conexión." });
        return;
      }
      const associationText = data.association && metaDraft.useTableeAssociatedSpend
        ? ` ${data.association.associatedAds} de ${data.association.accountAds} anuncios asociados a ${storefrontName}.`
        : "";
      setModalMessage({ tone: "success", text: `${data.account?.name ? `Meta conectado: ${data.account.name}.` : "Meta conectado."}${associationText}` });
    } catch (err) {
      setModalMessage({ tone: "error", text: "No se pudo probar la conexión con Meta." });
    } finally {
      setTesting(false);
    }
  }

  const loadMetaEventStatus = useCallback(async () => {
    try {
      const response = await fetch(apiUrl(`${apiPrefix}/marketing/meta/events/status`), { headers: getSessionHeaders() });
      const data = await response.json();
      if (data.success) setEventStatus((current) => ({ ...current, ...(data.counts || {}) }));
    } catch (_err) {
      // Status is operational context and should not block configuration.
    }
  }, [apiPrefix]);

  async function saveMetaConversions(event) {
    event.preventDefault();
    setSavingConversions(true);
    setModalMessage(null);
    try {
      const response = await fetch(apiUrl(`${apiPrefix}/marketing/integrations/meta/conversions`), {
        method: "PUT",
        headers: getSessionHeaders(),
        body: JSON.stringify(metaDraft),
      });
      const data = await response.json();
      if (!data.success) {
        setModalMessage({ tone: "error", text: data.message || "No se pudo guardar la medición de conversiones." });
        return;
      }
      const nextMeta = { ...emptyMetaIntegration, ...data.integration, appSecret: "", accessToken: "", conversionsAccessToken: "" };
      setMetaIntegration(nextMeta);
      setMetaDraft(nextMeta);
      setTestEventCode("");
      setConfigProvider("");
    } catch (_err) {
      setModalMessage({ tone: "error", text: "No se pudo guardar la medición de conversiones." });
    } finally {
      setSavingConversions(false);
    }
  }

  async function testMetaConversions() {
    setTestingConversions(true);
    setModalMessage(null);
    try {
      const response = await fetch(apiUrl(`${apiPrefix}/marketing/integrations/meta/conversions/test`), {
        method: "POST",
        headers: getSessionHeaders(),
        body: JSON.stringify({ ...metaDraft, testEventCode }),
      });
      const data = await response.json();
      if (!data.success) {
        setModalMessage({ tone: "error", text: data.message || "Meta rechazó el evento de prueba." });
        return;
      }
      setModalMessage({ tone: "success", text: `Evento recibido por Meta${data.eventsReceived ? ` (${data.eventsReceived})` : ""}. Guarda para aplicar.` });
      await loadMetaEventStatus();
    } catch (_err) {
      setModalMessage({ tone: "error", text: "No se pudo probar Conversions API." });
    } finally {
      setTestingConversions(false);
    }
  }

  async function retryMetaEvents() {
    try {
      await fetch(apiUrl(`${apiPrefix}/marketing/meta/events/retry`), { method: "POST", headers: getSessionHeaders() });
      await loadMetaEventStatus();
    } catch (_err) {
      setModalMessage({ tone: "error", text: "No se pudieron reintentar los eventos." });
    }
  }

  async function copyMetaMarketingUrl() {
    try {
      await navigator.clipboard.writeText(metaMarketingUrl);
      setMetaUrlCopied(true);
      window.setTimeout(() => setMetaUrlCopied(false), 1800);
    } catch (_err) {
      setMetaUrlCopied(false);
    }
  }

  const loadInsights = useCallback(async () => {
    setLoadingInsights(true);
    setMessage("");
    try {
      const params = new URLSearchParams({
        dateFrom: dateRange.dateFrom,
        dateTo: dateRange.dateTo,
      });
      const [metaResponse, tableeResponse] = await Promise.all([
        fetch(apiUrl(`${apiPrefix}/marketing/meta/insights?${params.toString()}`), { headers: getSessionHeaders() }),
        fetch(apiUrl(`${apiPrefix}/marketing/tablee-attribution?${params.toString()}&source=meta`), { headers: getSessionHeaders() }),
      ]);
      const [metaData, tableeData] = await Promise.all([metaResponse.json(), tableeResponse.json()]);
      if (!metaData.success) {
        setMessage(metaData.message || "No se pudo leer Meta Ads.");
        setInsights({ rows: [], adRows: [], totals: {}, scope: {} });
      } else {
        setInsights({ rows: metaData.rows || [], adRows: metaData.adRows || [], totals: metaData.totals || {}, scope: metaData.scope || {} });
      }
      if (!tableeData.success) {
        setMessage(tableeData.message || `No se pudo leer la atribución de ${storefrontName}.`);
        setAttribution({ rows: [], totals: {} });
      } else {
        setAttribution(filterAttributionForMetaScope(tableeData, metaData.rows || [], metaData.scope || {}));
      }
    } catch (err) {
      setMessage("No se pudieron cargar las métricas de marketing.");
    } finally {
      setLoadingInsights(false);
    }
  }, [dateRange.dateFrom, dateRange.dateTo, apiPrefix, storefrontName]);

  const loadGoogleInsights = useCallback(async () => {
    setLoadingInsights(true); setMessage("");
    try {
      const params = new URLSearchParams({ dateFrom: dateRange.dateFrom, dateTo: dateRange.dateTo });
      const [adsResponse, tableeResponse] = await Promise.all([
        fetch(apiUrl(`${apiPrefix}/marketing/google/insights?${params}`), { headers: getSessionHeaders() }),
        fetch(apiUrl(`${apiPrefix}/marketing/tablee-attribution?${params}&source=google`), { headers: getSessionHeaders() }),
      ]);
      const [ads, tablee] = await Promise.all([adsResponse.json(), tableeResponse.json()]);
      if (!ads.success) setMessage(ads.message || "No se pudo leer Google Ads.");
      setGoogleInsights(ads.success ? { rows: ads.rows || [], totals: ads.totals || {}, scope: ads.scope || {} } : { rows: [], totals: {}, scope: {} });
      setGoogleAttribution(tablee.success && ads.success ? filterAttributionForGoogleScope(tablee, ads.scope || {}) : { rows: [], totals: {} });
    } catch (_err) { setMessage("No se pudieron cargar las métricas de Google."); }
    finally { setLoadingInsights(false); }
  }, [dateRange.dateFrom, dateRange.dateTo, apiPrefix]);

  const loadVisitorAnalytics = useCallback(async () => {
    setLoadingInsights(true);
    setMessage("");
    try {
      const params = new URLSearchParams({
        dateFrom: dateRange.dateFrom,
        dateTo: dateRange.dateTo,
        excludeTests: "true",
      });
      const response = await fetch(apiUrl(`${apiPrefix}/marketing/visitor-analytics?${params}`), {
        headers: getSessionHeaders(),
      });
      const data = await response.json();
      if (!data.success) {
        setMessage(data.message || "No se pudo leer el tráfico del sitio.");
        return;
      }
      setVisitorAnalytics({
        totals: data.totals || {},
        funnel: data.funnel || [],
        topProducts: data.topProducts || [],
        topAreas: data.topAreas || [],
        sources: data.sources || [],
        landingSections: data.landingSections || [],
        landingClicks: data.landingClicks || [],
        landingFunnels: data.landingFunnels || [],
        checkoutSteps: data.checkoutSteps || [],
        checkoutIssues: data.checkoutIssues || [],
      });
    } catch (_err) {
      setMessage("No se pudo leer el tráfico del sitio.");
    } finally {
      setLoadingInsights(false);
    }
  }, [dateRange.dateFrom, dateRange.dateTo, apiPrefix]);

  useEffect(() => {
    loadIntegrations();
  }, [loadIntegrations]);

  useEffect(() => {
    if (activeProvider === "meta" && metaIntegration.isEnabled && metaIntegration.accessTokenConfigured && metaIntegration.adAccountId) {
      loadInsights();
    }
  }, [activeProvider, loadInsights, metaIntegration.isEnabled, metaIntegration.accessTokenConfigured, metaIntegration.adAccountId, metaIntegration.useTableeAssociatedSpend]);

  useEffect(() => {
    if (activeProvider === "google" && googleIntegration.isEnabled && googleIntegration.refreshTokenConfigured && googleIntegration.developerTokenConfigured && googleIntegration.customerId) loadGoogleInsights();
  }, [activeProvider, loadGoogleInsights, googleIntegration.isEnabled, googleIntegration.refreshTokenConfigured, googleIntegration.developerTokenConfigured, googleIntegration.customerId]);

  useEffect(() => {
    if (activeProvider === "site") loadVisitorAnalytics();
  }, [activeProvider, loadVisitorAnalytics]);

  useEffect(() => {
    if (!configProvider) {
      setModalMessage(null);
      setShowMetaUrl(false);
      setMetaUrlCopied(false);
      return undefined;
    }
    setModalMessage(null);
    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setMetaDraft({ ...emptyMetaIntegration, ...metaIntegration, appSecret: "", accessToken: "", conversionsAccessToken: "" });
        setTestEventCode("");
        setConfigProvider("");
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [configProvider, metaIntegration]);

  useEffect(() => {
    if (configProvider === "meta" && metaConfigTab === "conversions") loadMetaEventStatus();
  }, [configProvider, metaConfigTab, loadMetaEventStatus]);

  const metaReady = metaIntegration.isEnabled && metaIntegration.accessTokenConfigured && metaIntegration.adAccountId;
  const googleReady = googleIntegration.isEnabled && googleIntegration.refreshTokenConfigured && googleIntegration.developerTokenConfigured && googleIntegration.customerId;
  const selectedProvider = activeProvider === "meta" ? metaIntegration : googleIntegration;

  return (
    <section className="tablee-marketing">
      <header className="tablee-marketing__header">
        <img src={storeLogo} alt={storefrontName} />
        <div>
          <span>Integraciones</span>
          <h1>Marketing</h1>
          <p>Lee campañas, gasto y ventas atribuidas desde {storefrontName}.</p>
        </div>
      </header>

      <div className="tablee-marketing__workspace">
        <aside className="tablee-marketing__providers" aria-label="Integraciones de marketing">
          <div className="tablee-marketing__provider-row">
            <button
              type="button"
              className="tablee-marketing__provider"
              onClick={() => setActiveProvider("site")}
            >
              <span className="material-symbols-outlined">monitoring</span>
              <span>
                <strong>Tráfico del sitio</strong>
                <small>Visitas, productos y embudo</small>
              </span>
              <em className="is-success">Activo</em>
            </button>
          </div>
          <div className="tablee-marketing__provider-row">
            <button
              type="button"
              className="tablee-marketing__provider"
              onClick={() => setActiveProvider("meta")}
            >
              <span className="material-symbols-outlined">campaign</span>
              <span>
                <strong>Meta Ads</strong>
                <small>Campañas y rendimiento</small>
              </span>
              <em className={`is-${getStatusTone(metaIntegration.status)}`}>{getStatusLabel(metaIntegration.status)}</em>
            </button>
            <button type="button" className="tablee-marketing__provider-config" onClick={() => openMetaConfig("account")} aria-label="Configurar Meta Ads" title="Configurar Meta Ads">
              <span className="material-symbols-outlined">settings</span>
            </button>
          </div>
          <div className="tablee-marketing__provider-row">
            <button
              type="button"
              className="tablee-marketing__provider"
              onClick={() => setActiveProvider("google")}
            >
              <span className="material-symbols-outlined">ads_click</span>
              <span>
                <strong>Google Ads</strong>
                <small>Campañas, conversiones y GA4</small>
              </span>
              <em className={`is-${getStatusTone(googleIntegration.status)}`}>{getStatusLabel(googleIntegration.status)}</em>
            </button>
            <button type="button" className="tablee-marketing__provider-config" onClick={openGoogleConfig} aria-label="Configurar Google Ads" title="Configurar Google Ads">
              <span className="material-symbols-outlined">settings</span>
            </button>
          </div>
        </aside>

        <main className="tablee-marketing__panel">
          {activeProvider === "site" ? (
            <>
              <div className="tablee-marketing__panel-head">
                <div>
                  <h2>Tráfico del sitio</h2>
                  <p>Recorrido anónimo con consentimiento · pruebas de Camilo excluidas</p>
                </div>
              </div>
              <div className="tablee-marketing__insights tablee-marketing__insights--traffic">
                <div className="tablee-marketing__filters">
                  <label><span>Desde</span><input type="date" value={dateRange.dateFrom} onChange={(event) => setDateRange((current) => ({ ...current, dateFrom: event.target.value }))} /></label>
                  <label><span>Hasta</span><input type="date" value={dateRange.dateTo} onChange={(event) => setDateRange((current) => ({ ...current, dateTo: event.target.value }))} /></label>
                  <button type="button" className="tablee-marketing__primary tablee-marketing__refresh-button" onClick={loadVisitorAnalytics} disabled={loadingInsights}><span className="material-symbols-outlined">sync</span>{loadingInsights ? "Cargando..." : "Actualizar"}</button>
                </div>
                <div className="tablee-marketing__tabs tablee-marketing__traffic-tabs" role="tablist" aria-label="Secciones de tráfico">
                  {[
                    ["summary", "Resumen"],
                    ["products", "Productos"],
                    ["areas", "Áreas"],
                    ["sources", "Origen"],
                    ["checkout", "Checkout"],
                    ["landings", "Landings"],
                  ].map(([tab, label]) => (
                    <button
                      id={`traffic-tab-${tab}`}
                      key={tab}
                      type="button"
                      role="tab"
                      aria-selected={trafficTab === tab}
                      aria-controls={`traffic-panel-${tab}`}
                      className={trafficTab === tab ? "is-active" : ""}
                      onClick={() => setTrafficTab(tab)}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                {trafficTab === "summary" ? (
                  <section id="traffic-panel-summary" role="tabpanel" aria-labelledby="traffic-tab-summary" className="tablee-marketing__traffic-panel">
                    <div className="tablee-marketing__metrics">
                      <div><span>Visitantes</span><strong>{formatNumber(visitorAnalytics.totals?.visitors)}</strong></div>
                      <div><span>Sesiones</span><strong>{formatNumber(visitorAnalytics.totals?.sessions)}</strong></div>
                      <div><span>Productos vistos</span><strong>{formatNumber(visitorAnalytics.totals?.productViews)}</strong></div>
                      <div><span>Checkout</span><strong>{formatNumber(visitorAnalytics.totals?.checkoutStarts)}</strong></div>
                      <div><span>Chat abierto</span><strong>{formatNumber(visitorAnalytics.totals?.chatOpens)}</strong></div>
                      <div><span>Compras</span><strong>{formatNumber(visitorAnalytics.totals?.purchases)}</strong></div>
                    </div>
                    <h3 className="tablee-marketing__section-title">Embudo por sesiones</h3>
                    <div className="tablee-marketing__table-wrap">
                      <table className="tablee-marketing__table"><thead><tr><th>Etapa</th><th>Sesiones</th><th>Conversión desde visita</th></tr></thead><tbody>
                        {visitorAnalytics.funnel.map((row) => {
                          const visits = Number(visitorAnalytics.funnel[0]?.sessions || 0);
                          return <tr key={row.event}><td><strong>{row.label}</strong></td><td>{formatNumber(row.sessions)}</td><td>{visits ? formatPercent((Number(row.sessions || 0) / visits) * 100) : "—"}</td></tr>;
                        })}
                      </tbody></table>
                    </div>
                  </section>
                ) : null}

                {trafficTab === "products" ? (
                  <section id="traffic-panel-products" role="tabpanel" aria-labelledby="traffic-tab-products" className="tablee-marketing__traffic-panel">
                    <h3 className="tablee-marketing__section-title">Productos más vistos</h3>
                    <div className="tablee-marketing__table-wrap">
                      <table className="tablee-marketing__table"><thead><tr><th>Producto</th><th>Categoría</th><th>Vistas</th><th>Visitantes</th></tr></thead><tbody>
                        {visitorAnalytics.topProducts.map((row) => <tr key={row.productId}><td><strong>{row.productName}</strong><small>{row.productId}</small></td><td>{row.category || "—"}</td><td>{formatNumber(row.views)}</td><td>{formatNumber(row.visitors)}</td></tr>)}
                      </tbody></table>
                      {!visitorAnalytics.topProducts.length ? <p className="tablee-marketing__empty-table">Aún no hay vistas de producto registradas para este rango.</p> : null}
                    </div>
                  </section>
                ) : null}

                {trafficTab === "areas" ? (
                  <section id="traffic-panel-areas" role="tabpanel" aria-labelledby="traffic-tab-areas" className="tablee-marketing__traffic-panel">
                    <h3 className="tablee-marketing__section-title">Áreas más vistas</h3>
                    <div className="tablee-marketing__table-wrap">
                      <table className="tablee-marketing__table"><thead><tr><th>Área</th><th>Ruta</th><th>Vistas</th><th>Visitantes</th></tr></thead><tbody>
                        {visitorAnalytics.topAreas.map((row, index) => <tr key={`${row.pageType}-${row.pagePath}-${index}`}><td><strong>{row.pageType}</strong></td><td>{row.pagePath}</td><td>{formatNumber(row.views)}</td><td>{formatNumber(row.visitors)}</td></tr>)}
                      </tbody></table>
                      {!visitorAnalytics.topAreas.length ? <p className="tablee-marketing__empty-table">Aún no hay áreas visitadas para este rango.</p> : null}
                    </div>
                  </section>
                ) : null}

                {trafficTab === "sources" ? (
                  <section id="traffic-panel-sources" role="tabpanel" aria-labelledby="traffic-tab-sources" className="tablee-marketing__traffic-panel">
                    <h3 className="tablee-marketing__section-title">Origen y campaña</h3>
                    <div className="tablee-marketing__table-wrap">
                      <table className="tablee-marketing__table"><thead><tr><th>Origen</th><th>Campaña</th><th>Conjunto</th><th>Anuncio</th><th>Ubicación</th><th>Visitantes</th></tr></thead><tbody>
                        {visitorAnalytics.sources.map((row, index) => <tr key={`${row.source}-${row.campaignId}-${row.adId}-${index}`}><td><strong>{row.source}</strong><small>{row.medium}</small></td><td>{row.campaign}<small>{row.campaignId || "Sin ID"}</small></td><td>{row.adsetName || "—"}<small>{row.adsetId || ""}</small></td><td>{row.adName || "Sin anuncio"}<small>{row.adId || "Sin ID"}</small></td><td>{row.placement || "—"}</td><td>{formatNumber(row.visitors)}</td></tr>)}
                      </tbody></table>
                      {!visitorAnalytics.sources.length ? <p className="tablee-marketing__empty-table">Aún no hay fuentes de tráfico para este rango.</p> : null}
                    </div>
                  </section>
                ) : null}

                {trafficTab === "checkout" ? (
                  <section id="traffic-panel-checkout" role="tabpanel" aria-labelledby="traffic-tab-checkout" className="tablee-marketing__traffic-panel">
                    <div className="tablee-marketing__metrics">
                      <div><span>Inicios de checkout</span><strong>{formatNumber(visitorAnalytics.totals?.checkoutStarts)}</strong></div>
                      <div><span>Errores de validación</span><strong>{formatNumber(visitorAnalytics.totals?.checkoutValidationErrors)}</strong></div>
                      <div><span>Pagos iniciados</span><strong>{formatNumber(visitorAnalytics.totals?.paymentInitiations)}</strong></div>
                      <div><span>Compras aprobadas</span><strong>{formatNumber(visitorAnalytics.totals?.purchases)}</strong></div>
                    </div>

                    <h3 className="tablee-marketing__section-title">Avance por paso</h3>
                    <div className="tablee-marketing__table-wrap">
                      <table className="tablee-marketing__table"><thead><tr><th>Paso</th><th>Sesiones que lo vieron</th><th>Sesiones que completaron</th><th>Conversión del paso</th><th>Errores</th></tr></thead><tbody>
                        {visitorAnalytics.checkoutSteps.map((row) => (
                          <tr key={row.step}>
                            <td><strong>{getCheckoutStepLabel(row.step)}</strong><small>{row.step}</small></td>
                            <td>{formatNumber(row.viewSessions)}<small>{formatNumber(row.views)} vistas</small></td>
                            <td>{formatNumber(row.completionSessions)}<small>{formatNumber(row.completions)} eventos</small></td>
                            <td>{row.viewSessions ? formatPercent((row.completionSessions / row.viewSessions) * 100) : "—"}</td>
                            <td>{formatNumber(row.validationErrors)}<small>{formatNumber(row.validationErrorSessions)} sesiones</small></td>
                          </tr>
                        ))}
                      </tbody></table>
                      {!visitorAnalytics.checkoutSteps.length ? <p className="tablee-marketing__empty-table">La medición detallada aparecerá cuando una nueva sesión recorra el checkout.</p> : null}
                    </div>

                    <h3 className="tablee-marketing__section-title">Selecciones, retrocesos y problemas</h3>
                    <div className="tablee-marketing__table-wrap">
                      <table className="tablee-marketing__table"><thead><tr><th>Evento</th><th>Paso / motivo</th><th>Campos</th><th>Opción</th><th>Eventos</th><th>Sesiones</th></tr></thead><tbody>
                        {visitorAnalytics.checkoutIssues.map((row, index) => (
                          <tr key={`${row.event}-${row.step}-${row.reason}-${index}`}>
                            <td><strong>{getCheckoutEventLabel(row.event)}</strong><small>{row.event}</small></td>
                            <td>{getCheckoutStepLabel(row.step)}<small>{row.reason || "—"}{row.httpStatus ? ` · HTTP ${row.httpStatus}` : ""}</small></td>
                            <td>{row.fields?.length ? row.fields.join(", ") : "—"}</td>
                            <td>{row.deliveryOptionName || row.paymentMethod || "—"}</td>
                            <td>{formatNumber(row.events)}</td>
                            <td>{formatNumber(row.sessions)}<small>{formatNumber(row.visitors)} visitantes</small></td>
                          </tr>
                        ))}
                      </tbody></table>
                      {!visitorAnalytics.checkoutIssues.length ? <p className="tablee-marketing__empty-table">Aún no hay selecciones, retrocesos ni errores detallados para este rango.</p> : null}
                    </div>
                  </section>
                ) : null}

                {trafficTab === "landings" ? (
                  <section id="traffic-panel-landings" role="tabpanel" aria-labelledby="traffic-tab-landings" className="tablee-marketing__traffic-panel">
                    <h3 className="tablee-marketing__section-title">Embudo por landing</h3>
                    <div className="tablee-marketing__table-wrap">
                      <table className="tablee-marketing__table"><thead><tr><th>Landing</th><th>Sesiones</th><th>Producto</th><th>Carro</th><th>Checkout</th><th>Compra</th></tr></thead><tbody>
                        {visitorAnalytics.landingFunnels.map((row) => <tr key={row.landingPath}><td><strong>{row.landingPath}</strong><small>{formatNumber(row.visitors)} visitantes</small></td><td>{formatNumber(row.sessions)}</td><td>{formatNumber(row.productSessions)}<small>{row.sessions ? formatPercent((row.productSessions / row.sessions) * 100) : "—"}</small></td><td>{formatNumber(row.cartSessions)}<small>{row.sessions ? formatPercent((row.cartSessions / row.sessions) * 100) : "—"}</small></td><td>{formatNumber(row.checkoutSessions)}</td><td>{formatNumber(row.purchaseSessions)}</td></tr>)}
                      </tbody></table>
                      {!visitorAnalytics.landingFunnels.length ? <p className="tablee-marketing__empty-table">Aún no hay sesiones de landings registradas para este rango.</p> : null}
                    </div>

                    <h3 className="tablee-marketing__section-title">Visibilidad y atención por sección</h3>
                    <div className="tablee-marketing__table-wrap">
                      <table className="tablee-marketing__table"><thead><tr><th>Landing / sección</th><th>Tipo</th><th>Vistas</th><th>Visitantes</th><th>Tiempo activo</th></tr></thead><tbody>
                        {visitorAnalytics.landingSections.map((row) => <tr key={`${row.landingSlug}-${row.blockId}`}><td><strong>{row.blockTitle}</strong><small>{row.landingSlug} · {row.blockId}</small></td><td>{row.blockType}</td><td>{formatNumber(row.views)}</td><td>{formatNumber(row.visitors)}</td><td>{formatDuration(row.activeMilliseconds)}</td></tr>)}
                      </tbody></table>
                      {!visitorAnalytics.landingSections.length ? <p className="tablee-marketing__empty-table">Las secciones aparecerán cuando acumulen al menos un segundo con 50% de visibilidad.</p> : null}
                    </div>

                    <h3 className="tablee-marketing__section-title">Clicks de campaña</h3>
                    <div className="tablee-marketing__table-wrap">
                      <table className="tablee-marketing__table"><thead><tr><th>Elemento</th><th>Acción</th><th>Landing</th><th>Clicks</th><th>Visitantes</th></tr></thead><tbody>
                        {visitorAnalytics.landingClicks.map((row, index) => <tr key={`${row.event}-${row.blockId}-${row.action}-${index}`}><td><strong>{row.label}</strong><small>{row.event}</small></td><td>{row.action}</td><td>{row.landingSlug}</td><td>{formatNumber(row.clicks)}</td><td>{formatNumber(row.visitors)}</td></tr>)}
                      </tbody></table>
                      {!visitorAnalytics.landingClicks.length ? <p className="tablee-marketing__empty-table">Aún no hay clicks identificados en landings para este rango.</p> : null}
                    </div>
                  </section>
                ) : null}
              </div>
            </>
          ) : activeProvider === "google" ? (
            <>
              <div className="tablee-marketing__panel-head">
                <div>
                  <h2>Google Ads</h2>
                  <p>{selectedProvider?.customerId || "Configura una cuenta publicitaria"} · {getStatusLabel(googleIntegration.status)}</p>
                </div>
                <button type="button" className="tablee-marketing__icon-button" onClick={openGoogleConfig} aria-label="Configurar Google Ads" title="Configurar Google Ads">
                  <span className="material-symbols-outlined">settings</span>
                </button>
              </div>
              <div className="tablee-marketing__insights">
                <div className="tablee-marketing__filters">
                  <label><span>Desde</span><input type="date" value={dateRange.dateFrom} onChange={(event) => setDateRange((current) => ({ ...current, dateFrom: event.target.value }))} /></label>
                  <label><span>Hasta</span><input type="date" value={dateRange.dateTo} onChange={(event) => setDateRange((current) => ({ ...current, dateTo: event.target.value }))} /></label>
                  <button type="button" className="tablee-marketing__primary tablee-marketing__refresh-button" onClick={loadGoogleInsights} disabled={!googleReady || loadingInsights}><span className="material-symbols-outlined">sync</span>{loadingInsights ? "Cargando..." : "Actualizar"}</button>
                </div>
                {!googleReady ? <div className="tablee-marketing__empty-panel is-compact is-metrics-empty"><span className="material-symbols-outlined">lock</span><h3>Configura Google Ads primero</h3><p>Conecta OAuth y el Developer Token para leer campañas.</p><button type="button" className="tablee-marketing__secondary tablee-marketing__configure-empty" onClick={openGoogleConfig}>Configurar</button></div> : <>
                  <div className="tablee-marketing__metrics">
                    <div><span>Gasto Google</span><strong>{formatCurrency(googleInsights.totals?.spend)}</strong></div>
                    <div><span>Clics</span><strong>{formatNumber(googleInsights.totals?.clicks)}</strong></div>
                    <div><span>Órdenes {storefrontName}</span><strong>{formatNumber(googleAttribution.totals?.orders)}</strong></div>
                    <div><span>Venta {storefrontName}</span><strong>{formatCurrency(googleAttribution.totals?.revenue)}</strong></div>
                    <div><span>ROAS</span><strong>{formatRoas(googleAttribution.totals?.revenue, googleInsights.totals?.spend)}</strong></div>
                  </div>
                  <div className="tablee-marketing__table-wrap"><table className="tablee-marketing__table"><thead><tr><th>Campaña</th><th>Gasto</th><th>Imp.</th><th>Clics</th><th>CTR</th><th>CPC</th><th>Conv. Google</th><th>Órdenes</th><th>Venta</th><th>ROAS</th></tr></thead><tbody>
                    {googleCampaignRows.map((row, index) => <tr key={`${row.campaignId || row.campaignName}-${index}`}><td><strong>{row.campaignName}</strong><small>{row.campaignId || "Sin ID"}</small></td><td>{formatCurrency(row.spend)}</td><td>{formatNumber(row.impressions)}</td><td>{formatNumber(row.clicks)}</td><td>{formatPercent(row.ctr)}</td><td>{formatCurrency(row.cpc)}</td><td>{formatNumber(row.conversions)}</td><td>{formatNumber(row.tableeOrders)}</td><td>{formatCurrency(row.tableeRevenue)}</td><td>{formatRoas(row.tableeRevenue, row.spend)}</td></tr>)}
                  </tbody></table>{!googleCampaignRows.length ? <p className="tablee-marketing__empty-table">Sin campañas ni órdenes atribuidas para el rango seleccionado.</p> : null}</div>
                </>}
              </div>
            </>
          ) : (
            <>
              <div className="tablee-marketing__panel-head">
                <div>
                  <h2>Meta Ads</h2>
                  <p>
                    {selectedProvider?.adAccountExternalId || "Configura una cuenta publicitaria"} · {getMetaScopeLabel(metaIntegration, storefrontName)} · {getStatusLabel(metaIntegration.status)}
                  </p>
                </div>
                <button type="button" className="tablee-marketing__icon-button" onClick={() => openMetaConfig("account")} aria-label="Configurar Meta Ads" title="Configurar Meta Ads">
                  <span className="material-symbols-outlined">settings</span>
                </button>
              </div>

              <div className="tablee-marketing__insights">
                  <div className="tablee-marketing__filters">
                    <label>
                      <span>Desde</span>
                      <input type="date" value={dateRange.dateFrom} onChange={(event) => setDateRange((current) => ({ ...current, dateFrom: event.target.value }))} />
                    </label>
                    <label>
                      <span>Hasta</span>
                      <input type="date" value={dateRange.dateTo} onChange={(event) => setDateRange((current) => ({ ...current, dateTo: event.target.value }))} />
                    </label>
                    <button type="button" className="tablee-marketing__primary tablee-marketing__refresh-button" onClick={loadInsights} disabled={!metaReady || loadingInsights}>
                      <span className="material-symbols-outlined">sync</span>
                      {loadingInsights ? "Cargando..." : "Actualizar"}
                    </button>
                  </div>

                  {!metaReady ? (
                    <div className="tablee-marketing__empty-panel is-compact is-metrics-empty">
                      <span className="material-symbols-outlined">lock</span>
                      <h3>Configura Meta Ads primero</h3>
                      <p>Guarda un access token y una cuenta publicitaria para leer métricas.</p>
                      <button type="button" className="tablee-marketing__secondary tablee-marketing__configure-empty" onClick={() => openMetaConfig("account")}>
                        <span className="material-symbols-outlined">settings</span>
                        Configurar
                      </button>
                    </div>
                  ) : (
                    <>
                      {insights.scope?.mode === "tablee_associated" ? (
                        <div className="tablee-marketing__scope-summary">
                          <span className="material-symbols-outlined">filter_alt</span>
                          <div>
                            <strong>Gasto asociado a {storefrontName}</strong>
                            <small>
                              {insights.scope.matchedAds || 0} anuncios incluidos · {insights.scope.excludedAds || 0} excluidos · {formatCurrency(insights.scope.excludedSpend)} fuera del alcance {storefrontName}
                            </small>
                          </div>
                        </div>
                      ) : null}
                      <div className="tablee-marketing__metrics">
                        <div><span>Gasto Meta</span><strong>{formatCurrency(insights.totals?.spend)}</strong></div>
                        <div><span>Clics totales</span><strong>{formatNumber(insights.totals?.clicks)}</strong></div>
                        <div><span>Clics al enlace</span><strong>{formatNumber(insights.totals?.linkClicks)}</strong></div>
                        <div><span>Clics externos</span><strong>{formatNumber(insights.totals?.outboundClicks)}</strong></div>
                        <div><span>Visitas landing Meta</span><strong>{formatNumber(insights.totals?.landingPageViews)}</strong></div>
                        <div><span>Órdenes {storefrontName}</span><strong>{formatNumber(attribution.totals?.orders)}</strong></div>
                        <div><span>Venta {storefrontName}</span><strong>{formatCurrency(attribution.totals?.revenue)}</strong></div>
                        <div><span>ROAS</span><strong>{formatRoas(attribution.totals?.revenue, insights.totals?.spend)}</strong></div>
                      </div>

                      <div className="tablee-marketing__table-wrap">
                        <table className="tablee-marketing__table">
                          <thead>
                            <tr>
                              <th>Campaña</th>
                              <th>Gasto</th>
                              <th>Imp.</th>
                              <th>Clics</th>
                              <th>Enlace</th>
                              <th>Externos</th>
                              <th>Landing</th>
                              <th>CTR</th>
                              <th>CPC</th>
                              <th>Órdenes</th>
                              <th>Venta</th>
                              <th>ROAS</th>
                              <th>Match</th>
                            </tr>
                          </thead>
                          <tbody>
                            {campaignRows.map((row, index) => (
                              <tr key={`${row.campaignId || row.campaignName}-${index}`}>
                                <td>
                                  <strong>{row.campaignName}</strong>
                                  <small>{row.campaignId || "Sin ID"}</small>
                                </td>
                                <td>{formatCurrency(row.spend)}</td>
                                <td>{formatNumber(row.impressions)}</td>
                                <td>{formatNumber(row.clicks)}</td>
                                <td>{formatNumber(row.linkClicks)}</td>
                                <td>{formatNumber(row.outboundClicks)}</td>
                                <td>{formatNumber(row.landingPageViews)}</td>
                                <td>{formatPercent(row.ctr)}</td>
                                <td>{formatCurrency(row.cpc)}</td>
                                <td>{formatNumber(row.tableeOrders)}</td>
                                <td>{formatCurrency(row.tableeRevenue)}</td>
                                <td>{formatRoas(row.tableeRevenue, row.spend)}</td>
                                <td>{row.matchType || "sin match"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {!campaignRows.length ? (
                          <p className="tablee-marketing__empty-table">
                            {insights.scope?.mode === "tablee_associated"
                              ? `No se encontraron campañas asociadas a ${storefrontName} para el rango seleccionado.`
                              : "Sin campañas ni órdenes atribuidas para el rango seleccionado."}
                          </p>
                        ) : null}
                      </div>

                      {insights.adRows?.length ? (
                        <>
                          <h3 className="tablee-marketing__section-title">Rendimiento por anuncio</h3>
                          <div className="tablee-marketing__table-wrap">
                            <table className="tablee-marketing__table">
                              <thead><tr><th>Anuncio</th><th>Conjunto</th><th>Gasto</th><th>Imp.</th><th>Clics</th><th>Enlace</th><th>Externos</th><th>Landing</th></tr></thead>
                              <tbody>{insights.adRows.map((row, index) => (
                                <tr key={`${row.adId || row.adName}-${index}`}>
                                  <td><strong>{row.adName}</strong><small>{row.adId || "Sin ID"}</small></td>
                                  <td>{row.adsetName || "—"}<small>{row.adsetId || ""}</small></td>
                                  <td>{formatCurrency(row.spend)}</td>
                                  <td>{formatNumber(row.impressions)}</td>
                                  <td>{formatNumber(row.clicks)}</td>
                                  <td>{formatNumber(row.linkClicks)}</td>
                                  <td>{formatNumber(row.outboundClicks)}</td>
                                  <td>{formatNumber(row.landingPageViews)}</td>
                                </tr>
                              ))}</tbody>
                            </table>
                          </div>
                        </>
                      ) : null}
                    </>
                  )}
                </div>
            </>
          )}
        </main>
      </div>

      {configProvider === "meta" ? (
        <div className="tablee-marketing__modal-backdrop" role="presentation" onMouseDown={closeMetaConfig}>
          <form
            className="tablee-marketing__config-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="tablee-marketing-meta-config-title"
            onMouseDown={(event) => event.stopPropagation()}
            onSubmit={metaConfigTab === "account" ? saveMetaIntegration : saveMetaConversions}
          >
            <div className="tablee-marketing__modal-head">
              <div>
                <h2 id="tablee-marketing-meta-config-title">Configurar Meta Ads</h2>
                <p>{metaConfigTab === "account" ? "Credenciales y cuenta publicitaria para leer métricas." : "Pixel y eventos de compra para optimizar ventas."}</p>
              </div>
              <button type="button" className="tablee-marketing__icon-button" onClick={closeMetaConfig} aria-label="Cerrar configuración">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="tablee-marketing__modal-body">
              <div className="tablee-marketing__config-tabs" role="tablist" aria-label="Configuración de Meta">
                <button type="button" role="tab" aria-selected={metaConfigTab === "account"} className={metaConfigTab === "account" ? "is-active" : ""} onClick={() => { setMetaConfigTab("account"); setModalMessage(null); }}>
                  Cuenta Ads
                </button>
                <button type="button" role="tab" aria-selected={metaConfigTab === "conversions"} className={metaConfigTab === "conversions" ? "is-active" : ""} onClick={() => { setMetaConfigTab("conversions"); setModalMessage(null); }}>
                  Conversiones
                </button>
              </div>

              {metaConfigTab === "account" ? (
                <>
              <label className="tablee-marketing__switch">
                <input
                  type="checkbox"
                  checked={metaDraft.isEnabled}
                  onChange={(event) => setMetaDraft((current) => ({ ...current, isEnabled: event.target.checked }))}
                />
                <span />
                <strong>Activar lectura de Meta Ads</strong>
              </label>
              <small className="tablee-marketing__switch-help">Controla solo la lectura de campañas y métricas. Pixel y Conversions API se activan por separado.</small>

              <div className="tablee-marketing__fields">
                <label>
                  <span>App ID</span>
                  <input value={metaDraft.appId} onChange={(event) => setMetaDraft((current) => ({ ...current, appId: event.target.value }))} />
                </label>
                <label>
                  <span>App Secret</span>
                  <input
                    type="password"
                    autoComplete="new-password"
                    spellCheck={false}
                    value={metaDraft.appSecret}
                    placeholder={metaDraft.appSecretConfigured ? metaDraft.appSecretPreview : ""}
                    onChange={(event) => setMetaDraft((current) => ({ ...current, appSecret: event.target.value }))}
                  />
                </label>
                <label>
                  <span>Access Token / System User Token</span>
                  <input
                    type="password"
                    autoComplete="new-password"
                    spellCheck={false}
                    value={metaDraft.accessToken}
                    placeholder={metaDraft.accessTokenConfigured ? metaDraft.accessTokenPreview : ""}
                    onChange={(event) => setMetaDraft((current) => ({ ...current, accessToken: event.target.value }))}
                  />
                </label>
                <label>
                  <span>Ad Account ID</span>
                  <input
                    value={metaDraft.adAccountId}
                    placeholder="act_1234567890"
                    onChange={(event) => setMetaDraft((current) => ({
                      ...current,
                      adAccountId: event.target.value,
                    }))}
                  />
                </label>
                <label>
                  <span>Business ID</span>
                  <input value={metaDraft.businessId} onChange={(event) => setMetaDraft((current) => ({ ...current, businessId: event.target.value }))} />
                </label>
                <label>
                  <span>Graph API version</span>
                  <input value={metaDraft.graphApiVersion} onChange={(event) => setMetaDraft((current) => ({ ...current, graphApiVersion: event.target.value }))} />
                </label>
              </div>

              <div className="tablee-marketing__association-setting">
                <label className="tablee-marketing__switch">
                  <input
                    type="checkbox"
                    checked={metaDraft.useTableeAssociatedSpend}
                    onChange={(event) => setMetaDraft((current) => ({ ...current, useTableeAssociatedSpend: event.target.checked }))}
                  />
                  <span />
                  <strong>Limitar métricas a campañas asociadas a {storefrontName}</strong>
                </label>
                <small>Reconoce automáticamente anuncios por la URL de campaña o el Instagram configurado para {storefrontName}. Mantiene el gasto de campañas sin órdenes.</small>
              </div>

              <div className="tablee-marketing__meta-url-setting">
                <button type="button" className="tablee-marketing__secondary" onClick={() => setShowMetaUrl((current) => !current)}>
                  <span className="material-symbols-outlined">link</span>
                  {showMetaUrl ? "Ocultar URL para Meta Ads" : "Ver URL para Meta Ads"}
                </button>
                {showMetaUrl ? (
                  <div className="tablee-marketing__meta-url-value">
                    <code>{metaMarketingUrl}</code>
                    <button type="button" className="tablee-marketing__icon-button" onClick={copyMetaMarketingUrl} aria-label="Copiar URL para Meta Ads" title="Copiar URL">
                      <span className="material-symbols-outlined">{metaUrlCopied ? "check" : "content_copy"}</span>
                    </button>
                  </div>
                ) : null}
              </div>
                </>
              ) : (
                <div className="tablee-marketing__conversion-config">
                  <div className="tablee-marketing__fields">
                    <label>
                      <span>Pixel / Dataset ID</span>
                      <input
                        inputMode="numeric"
                        value={metaDraft.pixelId}
                        placeholder="123456789012345"
                        onChange={(event) => setMetaDraft((current) => ({ ...current, pixelId: event.target.value }))}
                      />
                    </label>
                    <label>
                      <span>Token de Conversions API</span>
                      <input
                        type="password"
                        autoComplete="new-password"
                        spellCheck={false}
                        value={metaDraft.conversionsAccessToken}
                        placeholder={metaDraft.conversionsAccessTokenConfigured ? "Configurado" : ""}
                        onChange={(event) => setMetaDraft((current) => ({ ...current, conversionsAccessToken: event.target.value }))}
                      />
                    </label>
                  </div>

                  <div className="tablee-marketing__conversion-switches">
                    <label className="tablee-marketing__switch">
                      <input
                        type="checkbox"
                        checked={metaDraft.browserPixelEnabled}
                        onChange={(event) => setMetaDraft((current) => ({ ...current, browserPixelEnabled: event.target.checked }))}
                      />
                      <span />
                      <strong>Activar Meta Pixel en {storefrontName}</strong>
                    </label>
                    <label className="tablee-marketing__switch">
                      <input
                        type="checkbox"
                        checked={metaDraft.conversionsApiEnabled}
                        onChange={(event) => setMetaDraft((current) => ({ ...current, conversionsApiEnabled: event.target.checked }))}
                      />
                      <span />
                      <strong>Enviar compras confirmadas por Conversions API</strong>
                    </label>
                    <small className="tablee-marketing__switch-help">Estas opciones controlan el seguimiento de conversiones aunque la lectura de métricas esté desactivada.</small>
                  </div>

                  <div className="tablee-marketing__test-events">
                    <label>
                      <span>Código de Test Events</span>
                      <input
                        value={testEventCode}
                        placeholder="TEST12345"
                        spellCheck={false}
                        onChange={(event) => setTestEventCode(event.target.value)}
                      />
                    </label>
                    <small>Se usa una sola vez para verificar el evento en Meta y no se guarda.</small>
                  </div>

                  <div className="tablee-marketing__event-status">
                    <div><span>Pendientes</span><strong>{eventStatus.pending + eventStatus.processing}</strong></div>
                    <div><span>Enviados</span><strong>{eventStatus.sent}</strong></div>
                    <div><span>Fallidos</span><strong>{eventStatus.failed + eventStatus.expired}</strong></div>
                    {eventStatus.failed ? (
                      <button type="button" className="tablee-marketing__secondary" onClick={retryMetaEvents}>Reintentar</button>
                    ) : null}
                  </div>
                </div>
              )}
            </div>

            <div className="tablee-marketing__modal-actions">
              {modalMessage ? (
                <p className={`tablee-marketing__modal-message is-${modalMessage.tone}`} role="status" aria-live="polite">
                  <span aria-hidden="true" />
                  {modalMessage.text}
                </p>
              ) : <span className="tablee-marketing__modal-actions-spacer" />}
              <button
                type="button"
                className="tablee-marketing__secondary"
                onClick={metaConfigTab === "account" ? testMetaConnection : testMetaConversions}
                disabled={testing || saving || testingConversions || savingConversions || loading}
              >
                <span className="material-symbols-outlined">wifi_tethering</span>
                {metaConfigTab === "account" ? (testing ? "Probando..." : "Probar cuenta") : (testingConversions ? "Probando..." : "Probar conversiones")}
              </button>
              <button type="submit" className="tablee-marketing__primary" disabled={saving || testing || savingConversions || testingConversions}>
                <span className="material-symbols-outlined">save</span>
                {saving || savingConversions ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {configProvider === "google" ? (
        <div className="tablee-marketing__modal-backdrop" role="presentation" onMouseDown={() => setConfigProvider("")}>
          <form
            className="tablee-marketing__config-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="tablee-marketing-google-config-title"
            onMouseDown={(event) => event.stopPropagation()}
            onSubmit={saveGoogleIntegration}
          >
            <div className="tablee-marketing__modal-head">
              <div>
                <h2 id="tablee-marketing-google-config-title">Configurar Google Ads</h2>
                <p>Campañas, GA4 y conversiones de compra de {storefrontName}.</p>
              </div>
              <button type="button" className="tablee-marketing__icon-button" onClick={() => setConfigProvider("")} aria-label="Cerrar configuración">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="tablee-marketing__modal-body">
              <label className="tablee-marketing__switch"><input type="checkbox" checked={googleDraft.isEnabled} onChange={(e) => setGoogleDraft((v) => ({ ...v, isEnabled: e.target.checked }))} /><span /><strong>Activar lectura de Google Ads</strong></label>
              <div className="tablee-marketing__fields">
                <label><span>Customer ID</span><input value={googleDraft.customerId} placeholder="123-456-7890" onChange={(e) => setGoogleDraft((v) => ({ ...v, customerId: e.target.value }))} /></label>
                <label><span>Manager Customer ID (opcional)</span><input value={googleDraft.managerCustomerId} onChange={(e) => setGoogleDraft((v) => ({ ...v, managerCustomerId: e.target.value }))} /></label>
                <label><span>OAuth Client ID</span><input value={googleDraft.clientId} onChange={(e) => setGoogleDraft((v) => ({ ...v, clientId: e.target.value }))} /></label>
                <label><span>ID campañas {storefrontName}</span><input value={googleDraft.tableeCampaignIds} placeholder={apiPrefix === "/tablee" ? "24030847091" : "IDs de campañas de esta tienda"} onChange={(e) => setGoogleDraft((v) => ({ ...v, tableeCampaignIds: e.target.value }))} /><small>Separa múltiples IDs con coma.</small></label>
                <label><span>OAuth Client Secret</span><input type="password" value={googleDraft.clientSecret} placeholder={googleDraft.clientSecretConfigured ? "Configurado" : ""} onChange={(e) => setGoogleDraft((v) => ({ ...v, clientSecret: e.target.value }))} /></label>
                <label><span>OAuth Refresh Token</span><input type="password" value={googleDraft.refreshToken} placeholder={googleDraft.refreshTokenConfigured ? "Configurado" : ""} onChange={(e) => setGoogleDraft((v) => ({ ...v, refreshToken: e.target.value }))} /></label>
                <label><span>Google Ads Developer Token</span><input type="password" value={googleDraft.developerToken} placeholder={googleDraft.developerTokenConfigured ? "Configurado" : ""} onChange={(e) => setGoogleDraft((v) => ({ ...v, developerToken: e.target.value }))} /></label>
                <label><span>GA4 Measurement ID</span><input value={googleDraft.measurementId} placeholder="G-XXXXXXXXXX" onChange={(e) => setGoogleDraft((v) => ({ ...v, measurementId: e.target.value }))} /></label>
                <label><span>Google Ads Conversion ID</span><input value={googleDraft.conversionId} placeholder="AW-123456789" onChange={(e) => setGoogleDraft((v) => ({ ...v, conversionId: e.target.value }))} /></label>
                <label><span>Etiqueta conversión Compra</span><input value={googleDraft.purchaseConversionLabel} onChange={(e) => setGoogleDraft((v) => ({ ...v, purchaseConversionLabel: e.target.value }))} /></label>
                <label><span>ID acción de conversión Compra</span><input value={googleDraft.conversionActionId} inputMode="numeric" placeholder="123456789" onChange={(e) => setGoogleDraft((v) => ({ ...v, conversionActionId: e.target.value }))} /><small>ID numérico usado por Google Data Manager.</small></label>
              </div>
              <div className="tablee-marketing__conversion-switches">
                <label className="tablee-marketing__switch"><input type="checkbox" checked={googleDraft.ga4Enabled} onChange={(e) => setGoogleDraft((v) => ({ ...v, ga4Enabled: e.target.checked }))} /><span /><strong>Activar Google Analytics 4</strong></label>
                <label className="tablee-marketing__switch"><input type="checkbox" checked={googleDraft.googleAdsEnabled} onChange={(e) => setGoogleDraft((v) => ({ ...v, googleAdsEnabled: e.target.checked }))} /><span /><strong>Activar conversiones de Google Ads</strong></label>
                <label className="tablee-marketing__switch"><input type="checkbox" checked={googleDraft.dataManagerEnabled} onChange={(e) => setGoogleDraft((v) => ({ ...v, dataManagerEnabled: e.target.checked }))} /><span /><strong>Activar respaldo con Google Data Manager</strong></label>
              </div>
            </div>
            <div className="tablee-marketing__modal-actions">
              {modalMessage ? <p className={`tablee-marketing__modal-message is-${modalMessage.tone}`} role="status"><span />{modalMessage.text}</p> : <span className="tablee-marketing__modal-actions-spacer" />}
              <button type="button" className="tablee-marketing__secondary" onClick={testGoogleConnection} disabled={testing || saving}><span className="material-symbols-outlined">wifi_tethering</span>{testing ? "Probando..." : "Probar cuenta"}</button>
              <button type="submit" className="tablee-marketing__primary" disabled={testing || saving}><span className="material-symbols-outlined">save</span>{saving ? "Guardando..." : "Guardar"}</button>
            </div>
          </form>
        </div>
      ) : null}
    </section>
  );
}

export default TableeMarketing;
